import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Download, Info, CalendarRange } from 'lucide-react';

/**
 * High5 — Haftalık KPI raporu (Total / UnPaid / Paid).
 *
 * Müşterinin elle tuttuğu Excel'in otomatik karşılığı: satırlar birebir aynı,
 * sütunlar ISO hafta (202629 = 2026'nın 29. haftası) + YTD.
 *
 * Kaynak Inbound AEM (inb_channel_performance) — /analytics/bq-dashboard ile
 * birebir aynı rakamlar. Tanımlar backend'de
 * (routes/high5WeeklyKpi.js): ECR = işlem/oturum · ASP = ciro/ADET ·
 * CPS = maliyet/paid oturum · ROAS = paid ciro/maliyet · FTW = ayakkabı adedi.
 */

const API_BASE = 'https://api.glowytics.com';

const fmtInt = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('tr-TR'));
const fmtMoney = (n) => (n == null ? '—' : `₺${Math.round(Number(n)).toLocaleString('tr-TR')}`);
const fmtPct = (n) => (n == null ? '—' : `%${Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtEcr = (n) => (n == null ? '—' : `%${Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtRatio = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtMoney2 = (n) => (n == null ? '—' : `₺${Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const dm = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}` : '');

// Blok tanımları — Excel'deki satır sırası birebir korunuyor.
// `extra: true` olanlar Excel'de yok, ASP'nin paydası görünsün diye eklendi.
const BLOCKS = [
  {
    key: 'total',
    label: 'Total',
    tone: 'bg-slate-50',
    rows: [
      { k: 'traffic', label: 'Traffic', f: fmtInt },
      { k: 'paidPct', label: 'Paid %', f: fmtPct },
      { k: 'unpaidPct', label: 'Unpaid %', f: fmtPct },
      { k: 'transactions', label: 'Transactions', f: fmtInt },
      { k: 'revenue', label: 'Revenue', f: fmtMoney },
      { k: 'ecr', label: 'ECR', f: fmtEcr },
      { k: 'asp', label: 'ASP', f: fmtMoney },
      { k: 'qty', label: 'Adet', f: fmtInt, extra: true },
      { k: 'ftwQty', label: 'FTW QTY', f: fmtInt },
    ],
  },
  {
    key: 'unpaid',
    label: 'UnPaid',
    tone: 'bg-emerald-50',
    rows: [
      { k: 'traffic', label: 'Traffic', f: fmtInt },
      { k: 'transactions', label: 'Transactions', f: fmtInt },
      { k: 'revenue', label: 'Revenue', f: fmtMoney },
      { k: 'ecr', label: 'ECR', f: fmtEcr },
      { k: 'asp', label: 'ASP', f: fmtMoney },
      { k: 'qty', label: 'Adet', f: fmtInt, extra: true },
    ],
  },
  {
    key: 'paid',
    label: 'Paid',
    tone: 'bg-indigo-50',
    rows: [
      { k: 'traffic', label: 'Traffic', f: fmtInt },
      { k: 'transactions', label: 'Transactions', f: fmtInt },
      { k: 'revenue', label: 'Revenue', f: fmtMoney },
      { k: 'ecr', label: 'ECR', f: fmtEcr },
      { k: 'asp', label: 'ASP', f: fmtMoney },
      { k: 'qty', label: 'Adet', f: fmtInt, extra: true },
      { k: 'cost', label: 'Cost', f: fmtMoney },
      { k: 'roas', label: 'ROAS', f: fmtRatio },
      { k: 'cps', label: 'CPS', f: fmtMoney2 },
      { k: 'cpo', label: 'Sipariş başı maliyet', f: fmtMoney, extra: true },
    ],
  },
];

/** Son iki hafta arasındaki değişim, %. */
const delta = (cur, prev) => {
  if (cur == null || prev == null || !(Math.abs(Number(prev)) > 0)) return null;
  return ((Number(cur) - Number(prev)) / Math.abs(Number(prev))) * 100;
};
const deltaTone = (v, invert) => {
  if (v == null) return 'text-gray-300';
  const good = invert ? v < 0 : v > 0;
  if (Math.abs(v) < 0.5) return 'text-gray-400';
  return good ? 'text-emerald-600' : 'text-red-600';
};
// Bu satırlarda ARTIŞ kötüdür (maliyet tarafı).
const INVERT = new Set(['cost', 'cps', 'cpo']);

function KpiCard({ title, value, sub, delta: d, invert }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{title}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
        {d != null && (
          <div className={`text-sm font-bold tabular-nums ${deltaTone(d, invert)}`}>
            {d > 0 ? '▲' : '▼'} %{Math.abs(d).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}
          </div>
        )}
      </div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

export default function High5WeeklyKpi() {
  const { selectedBrand } = useAuth();
  const [weeksWanted, setWeeksWanted] = useState(12);
  const [year, setYear] = useState('');
  const [showExtra, setShowExtra] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (w = weeksWanted, y = year) => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const qs = `?weeks=${w}${y ? `&year=${y}` : ''}`;
      const res = await fetch(`${API_BASE}/api/high5-weekly-kpi${qs}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || `HTTP ${res.status}`);
      }
      const j = await res.json();
      setData(j);
      setYear(String(j.isoYear));
    } catch (e) {
      setData(null);
      setError(e?.message || 'Veri alınamadı');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id, weeksWanted, year]);

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [selectedBrand?.id]);

  const runSync = async () => {
    if (!selectedBrand?.id) return;
    try {
      setSyncing(true);
      const res = await fetch(`${API_BASE}/api/high5-weekly-kpi/sync`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchData();
    } catch (e) {
      setError(e?.message || 'Senkron başarısız');
    } finally {
      setSyncing(false);
    }
  };

  const weeks = useMemo(() => data?.weeks || [], [data]);
  const last = weeks.length ? weeks[weeks.length - 1] : null;
  const prev = weeks.length > 1 ? weeks[weeks.length - 2] : null;

  const cell = (row, block, rowKey) => (row ? row[block]?.[rowKey] : null);

  const csv = () => {
    if (!data) return;
    const head = ['Blok', 'KPI', ...weeks.map((w) => w.key), 'YTD'];
    const lines = [head.join(';')];
    for (const b of BLOCKS) {
      for (const r of b.rows) {
        if (r.extra && !showExtra) continue;
        const vals = weeks.map((w) => {
          const v = cell(w, b.key, r.k);
          return v == null ? '' : String(v).replace('.', ',');
        });
        const y = cell(data.ytd, b.key, r.k);
        lines.push([b.label, r.label, ...vals, y == null ? '' : String(y).replace('.', ',')].join(';'));
      }
    }
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `high5-haftalik-kpi-${data.isoYear}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-6 space-y-5 max-w-full">
      {/* Başlık + kontroller */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-indigo-600" />
            Haftalık KPI — Total / UnPaid / Paid
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Kaynak: Inbound AEM (BQ Dashboard ile aynı) · Maliyet: AEM reklam maliyeti
            {data?.lastDataDate && <> · Son veri günü <strong>{data.lastDataDate}</strong></>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
            value={weeksWanted}
            onChange={(e) => { const v = Number(e.target.value); setWeeksWanted(v); fetchData(v, year); }}
          >
            {[8, 12, 20, 26, 40, 53].map((n) => <option key={n} value={n}>Son {n} hafta</option>)}
          </select>
          {(data?.availableYears?.length || 0) > 1 && (
            <select
              className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
              value={year}
              onChange={(e) => { setYear(e.target.value); fetchData(weeksWanted, e.target.value); }}
            >
              {data.availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          <label className="text-xs text-gray-600 flex items-center gap-1.5 px-2">
            <input type="checkbox" checked={showExtra} onChange={(e) => setShowExtra(e.target.checked)} />
            Ek satırlar
          </label>
          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </button>
          <button
            onClick={runSync}
            disabled={syncing}
            title="FTW adedi için BigQuery'den son 10 günü yeniden çeker (cron zaten 07:00'de çalışıyor)"
            className="text-sm px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> BQ senkron
          </button>
          <button
            onClick={csv}
            disabled={!data}
            className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>
      )}

      {/* Son tam haftanın kartları */}
      {last && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <KpiCard title={`Traffic · ${last.key}`} value={fmtInt(last.total.traffic)}
            sub={`Paid ${fmtPct(last.total.paidPct)}`} delta={delta(last.total.traffic, prev?.total?.traffic)} />
          <KpiCard title="Transactions" value={fmtInt(last.total.transactions)}
            sub={`ECR ${fmtEcr(last.total.ecr)}`} delta={delta(last.total.transactions, prev?.total?.transactions)} />
          <KpiCard title="Revenue" value={fmtMoney(last.total.revenue)}
            sub={`ASP ${fmtMoney(last.total.asp)}`} delta={delta(last.total.revenue, prev?.total?.revenue)} />
          <KpiCard title="Cost" value={fmtMoney(last.paid.cost)}
            sub={Object.entries(last.cost?.byPlatform || {}).map(([k, v]) => `${k}: ${fmtMoney(v)}`).join(' · ')}
            delta={delta(last.paid.cost, prev?.paid?.cost)} invert />
          <KpiCard title="ROAS (paid)" value={fmtRatio(last.paid.roas)}
            sub={`CPS ${fmtMoney2(last.paid.cps)}`} delta={delta(last.paid.roas, prev?.paid?.roas)} />
          <KpiCard title="FTW QTY" value={fmtInt(last.total.ftwQty)}
            sub={`Toplam adet ${fmtInt(last.total.qty)}`} delta={delta(last.total.ftwQty, prev?.total?.ftwQty)} />
        </div>
      )}

      {/* Bloklar */}
      {BLOCKS.map((b) => (
        <Card key={b.key} className="p-0 overflow-hidden">
          <div className={`px-4 py-2 ${b.tone} border-b border-gray-200 flex items-center justify-between`}>
            <div className="font-bold text-gray-800">{b.label}</div>
            {b.key === 'paid' && (
              <div className="text-[11px] text-gray-500">ROAS = paid ciro ÷ maliyet · CPS = maliyet ÷ paid oturum</div>
            )}
            {b.key === 'total' && (
              <div className="text-[11px] text-gray-500">ECR = işlem ÷ oturum · ASP = ciro ÷ adet</div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-[#1F3864] text-white">
                  <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-[#1F3864] z-10">KPI's</th>
                  {weeks.map((w) => (
                    <th key={w.key} className="text-right px-3 py-2 font-semibold whitespace-nowrap" title={`${w.start} → ${w.end}`}>
                      {w.key}
                      {w.partial && <span className="ml-1 text-amber-300" title={`${w.days}/7 gün`}>*</span>}
                      <div className="text-[10px] font-normal opacity-70">{dm(w.start)}–{dm(w.end)}</div>
                    </th>
                  ))}
                  <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Δ son hafta</th>
                  <th className="text-right px-3 py-2 font-semibold whitespace-nowrap bg-[#16294a]">YTD</th>
                </tr>
              </thead>
              <tbody>
                {b.rows.filter((r) => showExtra || !r.extra).map((r, i) => {
                  const d = delta(cell(last, b.key, r.k), cell(prev, b.key, r.k));
                  return (
                    <tr key={r.k} className={i % 2 ? 'bg-gray-50/60' : 'bg-white'}>
                      <td className={`px-3 py-1.5 font-medium text-gray-700 sticky left-0 z-10 ${i % 2 ? 'bg-gray-50' : 'bg-white'} ${r.extra ? 'italic text-gray-500' : ''}`}>
                        {r.label}{r.extra && <span className="ml-1 text-[10px] text-gray-400">+</span>}
                      </td>
                      {weeks.map((w) => (
                        <td key={w.key} className="px-3 py-1.5 text-right tabular-nums text-gray-800 whitespace-nowrap">
                          {r.f(cell(w, b.key, r.k))}
                        </td>
                      ))}
                      <td className={`px-3 py-1.5 text-right tabular-nums font-semibold whitespace-nowrap ${deltaTone(d, INVERT.has(r.k))}`}>
                        {d == null ? '—' : `${d > 0 ? '+' : ''}${d.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}%`}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-bold text-gray-900 bg-slate-50 whitespace-nowrap">
                        {r.f(cell(data?.ytd, b.key, r.k))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {/* Yöntem notu */}
      <Card className="p-4">
        <div className="flex items-start gap-2 text-sm text-gray-600">
          <Info className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
          <div className="space-y-1.5">
            <div>
              <strong>Kaynak</strong> Inbound AEM kanal tablosu — <code>/analytics/bq-dashboard</code> ile aynı tablo
              ve aynı süzgeç, oturum / işlem / ciro iki sayfada birebir aynı. Çerez onayı vermeyen trafik modellenmiş
              olarak dahil; müşterinin Excel’iyle de tutuyor (29. hafta: 39.441 oturum · 167 sipariş · ₺1.839.150 ·
              230 adet · paid %80,98 · ROAS 4,19).
            </div>
            <div>
              <strong>Paid / UnPaid ayrımı</strong> AEM’in kanal ailesinden (<code>channel_family</code>):
              Google Ads, Paid Social, Media Buying, Retargeting, Other Paid → paid; kalanı unpaid.
            </div>
            <div>
              <strong>Maliyet</strong> aynı tablonun reklam maliyetinden, platform kırılımı kartta görünür.
            </div>
            <div>
              <strong>FTW QTY</strong> = ayakkabı adedi. AEM ürün kategorisi taşımadığı için yalnız bu satır ham GA4
              BigQuery olaylarından (kategoride “Ayakkabı/Footwear” geçen satırlar), günde bir 07:00’de tazelenir.
              Bugünün yarım günü tabloya girmiyor.
              <span className="text-amber-700"> * işaretli hafta eksik gün içerir.</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
